/**
 * TEF Canada oral expression with the live examiner (D-109), as the official pages describe it:
 * face to face and recorded, 15 minutes. Section A (5 min): the learner phones about a document
 * (advert, classified ad, job or leisure offer) and asks about ten questions; the examiner answers
 * as the person behind the document (vous). Section B (10 min): the learner presents the activity
 * of a document to a friend and tries to convince them; the examiner is that friend and never
 * agrees (tu). No preparation time: the document stays on screen. Every turn is sent to the
 * server (transcribed, answered in French); the whole section is also recorded and sent.
 */

import { academyAttemptsNextCreate, academyAttemptsRecordingCreate, academyAttemptsSpeakingCreate } from "@agencyos/api-client";
import { type IeltsTask, type TefState, type TurnListening, listenTurn, questionsAsked, startTurn } from "@agencyos/core";
import { Badge, Button } from "@agencyos/ui-web";
import { MessageSquareReply, Mic, MicOff, Phone, Square, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { closeStream, extensionFor, openMicrophone, recorderMimeType, recorderOptions, sendRecording, speakFrench, stopSpeaking, useMicLevel, useMicStayedSilent} from "../audio";
import { usePhaseCountdown } from "../shared";
import { Examiner, LevelMeter } from "../SpeakingSection";
import { clock } from "../text";
import { Ring } from "../ui";
import { isNoOralCredits } from "@agencyos/core";
import { OralCreditsNotice, reportNoOralCredits } from "../TrainerReview";

const VOICE_LEVEL = 0.06;
type Phase = "mic" | "ready" | "opening" | "record" | "upload" | "uploadError" | "between" | "review";
type LiveStatus = "idle" | "listening" | "sending" | "speaking";
interface Line {
  role: "learner" | "examiner";
  text: string;
}

function useTefExaminer({ attemptId, task, stream, level, active, onEnd }: { attemptId: string; task: IeltsTask | undefined; stream: MediaStream | null; level: number; active: boolean; onEnd: () => void }) {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [lines, setLines] = useState<Line[]>([]);
  const [offline, setOffline] = useState(false);
  const segment = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const listening = useRef<TurnListening>(startTurn(0));
  const segmentStart = useRef(0);
  const sectionStart = useRef(0);
  const activeRef = useRef(active);
  const statusRef = useRef<LiveStatus>("idle");
  const endRef = useRef(onEnd);
  useEffect(() => {
    endRef.current = onEnd;
  }, [onEnd]);
  const change = (next: LiveStatus) => {
    statusRef.current = next;
    setStatus(next);
  };

  const startSegment = useCallback(() => {
    if (!stream || !activeRef.current || typeof MediaRecorder === "undefined") return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    segment.current = media;
    segmentStart.current = Date.now();
    listening.current = startTurn(segmentStart.current);
    media.start();
    change("listening");
  }, [stream]);

  const answer = useCallback(
    async (blob: Blob) => {
      if (!task) return;
      change("sending");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `tef-turn.${extensionFor(type)}`, { type });
      const offset = Math.max((segmentStart.current - sectionStart.current) / 1000, 0);
      let reply = "";
      let end = false;
      try {
        const response = await academyAttemptsSpeakingCreate(attemptId, { task: task.id, audio: file, offset });
        const data = response.data;
        reply = data.reply;
        end = data.end;
        setOffline(false);
        setLines((list) => [
          ...list,
          ...(data.transcript ? [{ role: "learner" as const, text: data.transcript }] : []),
          ...(reply ? [{ role: "examiner" as const, text: reply }] : []),
        ]);
      } catch (error) {
        if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
        setOffline(true);
      }
      if (!activeRef.current) return;
      if (reply) {
        change("speaking");
        await speakFrench(reply);
      }
      if (!activeRef.current) return;
      if (end) {
        change("idle");
        endRef.current();
      } else startSegment();
    },
    [attemptId, task, startSegment],
  );

  const finishTurn = useCallback(() => {
    const media = segment.current;
    if (!media || media.state !== "recording" || statusRef.current !== "listening") return;
    media.onstop = () => void answer(new Blob(chunks.current, { type: media.mimeType || "audio/webm" }));
    media.stop();
  }, [answer]);

  useEffect(() => {
    activeRef.current = active;
    if (active) {
      sectionStart.current = Date.now();
      startSegment();
      return;
    }
    const media = segment.current;
    if (media && media.state === "recording") {
      media.onstop = null;
      media.stop();
    }
    segment.current = null;
    change("idle");
  }, [active, startSegment]);

  useEffect(
    () => () => {
      activeRef.current = false;
      const media = segment.current;
      if (media && media.state === "recording") {
        media.onstop = null;
        media.stop();
      }
    },
    [],
  );

  useEffect(() => {
    if (!active || status !== "listening") return;
    const next = listenTurn(listening.current, level > VOICE_LEVEL, Date.now());
    listening.current = next;
    if (next.end) finishTurn();
  }, [level, active, status, finishTurn]);

  return { status, lines, setLines, offline, finishTurn };
}

export function TefSpeaking({ attemptId, state, onChanged }: { attemptId: string; state: TefState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micError, setMicError] = useState(false);
  const [phase, setPhase] = useState<Phase>("mic");
  const [done, setDone] = useState<string[]>(() => state.tasks.filter((task) => task.hasAudio).map((task) => task.id));
  const [opening, setOpening] = useState("");
  const [finishing, setFinishing] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const pendingBlob = useRef<Blob | null>(null);
  const level = useMicLevel(stream);
  const micSilent = useMicStayedSilent(level, phase === "record");

  const task = state.tasks.find((item) => !done.includes(item.id));
  const info = state.tefTasks.find((item) => item.id === task?.id);
  const letter = info?.section || (task?.number === 2 ? "B" : "A");
  const seconds = task?.seconds ?? 0;

  useEffect(
    () => () => {
      stopSpeaking();
      if (recorder.current?.state === "recording") recorder.current.stop();
    },
    [],
  );
  useEffect(() => () => closeStream(stream), [stream]);
  // The last task is over: the microphone is released at once (D-114), not when leaving.
  useEffect(() => {
    if (phase === "review") setStream(null);
  }, [phase]);

  const testMicrophone = async () => {
    setMicError(false);
    try {
      setStream(await openMicrophone());
      setPhase("ready");
    } catch {
      setMicError(true);
    }
  };

  const upload = useCallback(
    async (blob: Blob, current: IeltsTask) => {
      setPhase("upload");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `tef-eo-${current.number}.${extensionFor(type)}`, { type });
      try {
        await sendRecording(() => academyAttemptsRecordingCreate(attemptId, { task: current.id, audio: file }));
        pendingBlob.current = null;
        setDone((list) => [...list, current.id]);
        setPhase("between");
      } catch {
        pendingBlob.current = blob;
        setPhase("uploadError");
      }
    },
    [attemptId],
  );

  const startRecording = useCallback(() => {
    if (!stream || !task) return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, recorderOptions(mimeType));
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    const current = task;
    media.onstop = () => void upload(new Blob(chunks.current, { type: media.mimeType || mimeType || "audio/webm" }), current);
    recorder.current = media;
    media.start(1000);
    setPhase("record");
  }, [stream, task, upload]);

  const stopRecording = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const live = useTefExaminer({ attemptId, task, stream, level, active: phase === "record", onEnd: stopRecording });
  const { setLines } = live;

  const beginSection = useCallback(async () => {
    if (!task) return;
    setPhase("opening");
    let reply = "";
    try {
      reply = (await academyAttemptsSpeakingCreate(attemptId, { task: task.id })).data.reply;
    } catch (error) {
      if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
      reply = "";
    }
    setOpening(reply);
    setLines(reply ? [{ role: "examiner", text: reply }] : []);
    await speakFrench(reply);
    startRecording();
  }, [attemptId, task, startRecording, setLines]);

  useEffect(() => {
    if (phase !== "between") return;
    const timer = window.setTimeout(() => {
      if (task) void beginSection();
      else setPhase("review");
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [phase, task, beginSection]);

  const left = usePhaseCountdown(seconds, phase === "record", stopRecording);
  const asked = questionsAsked(live.lines);

  const finish = async () => {
    setFinishing(true);
    try {
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  const noRecorder = typeof MediaRecorder === "undefined";

  return (
    <section className="ac-speaking" aria-label={t("tef.skill.eo")}>
      <div className="ac-question__sheet ac-speaking__sheet">
        <OralCreditsNotice />
        {phase === "mic" && (
          <div className="ac-stage">
            <span className="ac-stage__mic" aria-hidden="true">
              <Mic size={34} strokeWidth={1.8} />
            </span>
            <h2 className="ac-title-2">{t("academy.speaking.micTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("tef.speaking.micHint")}</p>
            {noRecorder ? (
              <p className="ko">{t("academy.speaking.noRecorder")}</p>
            ) : (
              <Button size="lg" icon={<Mic aria-hidden size={18} />} onClick={() => void testMicrophone()}>
                {t("academy.speaking.micTest")}
              </Button>
            )}
            {micError && (
              <p className="ko ac-status" role="alert">
                <MicOff aria-hidden size={18} />
                {t("academy.speaking.micDenied")}
              </p>
            )}
          </div>
        )}

        {phase === "ready" && (
          <div className="ac-stage">
            <h2 className="ac-title-2">{t("tef.speaking.readyTitle")}</h2>
            <LevelMeter level={level} label={t("academy.speaking.level")} />
            <ul className="ac-eo-tasks">
              {state.tasks.map((item) => {
                const itemLetter = item.number === 2 ? "B" : "A";
                return (
                  <li key={item.id}>
                    <span className="ac-task-tab__n ao-num" aria-hidden="true">
                      {itemLetter}
                    </span>
                    <span>{t(itemLetter === "A" ? "tef.speaking.aTitle" : "tef.speaking.bTitle")}</span>
                    <span className="ac-caption ao-num">{clock(item.seconds)}</span>
                  </li>
                );
              })}
            </ul>
            <p className="ac-footnote ac-stage__text">{t("tef.speaking.rules")}</p>
            {task ? (
              <Button size="lg" onClick={() => void beginSection()}>
                {t("academy.speaking.start")}
              </Button>
            ) : (
              <Button size="lg" onClick={() => setPhase("review")}>
                {t("academy.player.continue")}
              </Button>
            )}
          </div>
        )}

        {task && (phase === "opening" || phase === "record") && (
          <div className="ac-stage ac-stage--task">
            <p className="ac-player__eyebrow">
              {letter === "A" ? <Phone aria-hidden size={14} /> : <Users aria-hidden size={14} />} {t(letter === "A" ? "tef.speaking.aTitle" : "tef.speaking.bTitle")}
            </p>
            {(info?.documents ?? []).map((doc, index) => (
              <article key={index} className="ac-cue-card ac-tef-document" lang="fr">
                {doc.title && <h2 className="ac-cue-card__topic">{doc.title}</h2>}
                <p>{doc.text}</p>
              </article>
            ))}
            <p className="ac-prompt" lang="fr">
              {task.prompt}
            </p>
            {info?.role && <Badge>{t("tef.speaking.role", { role: info.role })}</Badge>}
            {phase === "opening" && (
              <div role="status" className="ac-stage__status">
                <Examiner speaking label={t("tef.speaking.examinerSpeaks")} />
                {opening && (
                  <p className="ac-bubble ac-bubble--them" lang="fr">
                    {opening}
                  </p>
                )}
              </div>
            )}
            {micSilent && <p className="ac-note ac-note--warning" role="alert">{t("academy.redo.micSilent")}</p>}
            {phase === "record" && (
              <>
                <div className={`ac-countdown ac-countdown--record${left <= 10 ? " ac-countdown--end" : ""}`}>
                  <Ring ratio={seconds ? left / seconds : 0} size={160} stroke={12} tone="eo">
                    <span className="ac-countdown__state">
                      <span className="ac-rec-dot" aria-hidden="true" />
                      <span role="status">{t("academy.speaking.recording")}</span>
                    </span>
                    <span className="ac-countdown__digits ao-num" role="timer" aria-label={t("academy.speaking.timeLeft")}>
                      {clock(left)}
                    </span>
                  </Ring>
                </div>
                {letter === "A" && <p className="ac-footnote ao-num">{t("tef.speaking.asked", { count: asked })}</p>}
                <LevelMeter level={level} label={t("academy.speaking.level")} />
                <div className="ac-live-examiner" aria-live="polite">
                  <p className="ac-status ac-secondary" role="status">
                    {live.status !== "listening" && <span className="ac-dot ac-dot--busy" aria-hidden="true" />}
                    {t(`tef.speaking.status.${live.status}`)}
                  </p>
                  {live.offline && <p className="ac-footnote">{t("academyExaminer.offline")}</p>}
                  {live.lines.length > 0 && (
                    <ul className="ac-transcript" aria-label={t("academyExaminer.conversation")}>
                      {live.lines.map((line, index) => (
                        <li key={`${index}-${line.role}`} className={`ac-bubble ac-bubble--${line.role === "examiner" ? "them" : "me"}`}>
                          <strong className="ac-bubble__who">{t(`tef.speaking.who.${line.role}`)}</strong> <span lang="fr">{line.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button icon={<MessageSquareReply aria-hidden size={16} />} onClick={live.finishTurn} disabled={live.status !== "listening"}>
                    {t("tef.speaking.finishedTurn")}
                  </Button>
                </div>
                <Button variant="ghost" icon={<Square aria-hidden size={14} />} onClick={stopRecording}>
                  {t("tef.speaking.endSection")}
                </Button>
              </>
            )}
          </div>
        )}

        {phase === "upload" && (
          <div className="ac-stage">
            <p className="ac-status" role="status">
              <span className="ac-dot ac-dot--busy" aria-hidden="true" />
              {t("academy.speaking.uploading")}
            </p>
          </div>
        )}
        {phase === "uploadError" && task && (
          <div className="ac-stage" role="alert">
            <p className="ko">{t("academy.speaking.uploadError")}</p>
            <Button onClick={() => pendingBlob.current && void upload(pendingBlob.current, task)}>{t("common.retry")}</Button>
          </div>
        )}
        {phase === "between" && (
          <div className="ac-stage">
            <p className="ac-status ac-status--success" role="status">
              {t("tef.speaking.between")}
            </p>
          </div>
        )}
        {phase === "review" && (
          <div className="ac-stage ac-stage--review">
            <h2 className="ac-title-2">{t("tef.speaking.doneTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("tef.speaking.doneText")}</p>
            <Button size="lg" onClick={() => void finish()} disabled={finishing}>
              {finishing ? t("common.loading") : t("academy.speaking.finish")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
